import React from 'react';
import truncate from 'lodash.truncate';
import { Document } from '@react-pdf/renderer';

import { FiClipboard } from 'react-icons/fi';
import ModalLayout from '@/layouts/ModalLayout';

import {
  EncaminhamentoEspecialista,
  OrientacaoMedica,
} from '@/models/Solicitacao';

import Button from '@/components/Button';
import Markdown from '@/components/Markdown';
import CheckboxLabel from '@/components/CheckboxLabel';

import { codeableConceptDisplay } from '@/features/fhir/helpers/fhirpath';
import TipoArtefatoLabel from '@/features/prescricao-livre/components/TipoArtefatoLabel';
import { documentPdf } from '@/features/pdf/helpers/print';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';
import DocumentPageResumoAtendimento from '@/features/pdf/documents/DocumentPageResumoAtendimento';

import DocumentPageOrientacao from '@/features/pdf/documents/DocumentPageOrientacao';
import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import DocumentPageEncaminhamento from '@/features/pdf/documents/DocumentPageEncaminhamento';

import {
  PrintSection,
  PrintItemTitle,
  PrintItemSubtitle,
  PrintItemContent,
  PrintSectionDivider,
  PrintActions,
} from './styles';
import { useExecucaoContext } from '../../providers/ExecucaoProvider';
import { useSolicitacoes } from '../../providers/SolicitacoesProvider';

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
}

const PrintItem: React.FC<{
  title: string;
  subtitle?: string;
  selected: boolean;
  onClick: () => void;
}> = ({ title, subtitle, selected, onClick }) => {
  return (
    <CheckboxLabel
      style={{ flex: 'none' }}
      state={selected ? 'marked' : 'unmarked'}
      size="small"
      onClick={onClick}
    >
      <PrintItemContent>
        <PrintItemTitle>{title}</PrintItemTitle>
        {subtitle && (
          <PrintItemSubtitle>
            <Markdown text={subtitle} />
          </PrintItemSubtitle>
        )}
      </PrintItemContent>
    </CheckboxLabel>
  );
};

const ModalPrintSumario: React.FC<Props> = ({ isOpen = false, onClose }) => {
  const [{ protocoloExecutado }] = useExecucaoContext();
  const { solicitacoes } = useSolicitacoes();
  const [{ encounter }] = useEnterpriseContext();
  const { user } = useAuth();

  const [exclude, setExclude] = React.useState<(string | number)[]>([]);
  const [loadingPdf, setLoadingPdf] = React.useState(false);

  // Limpa exclusões ao fechar o modal
  React.useEffect(() => {
    if (!isOpen) {
      setExclude([]);
      setLoadingPdf(false);
    }
  }, [isOpen]);

  const sumario = React.useMemo(() => {
    return protocoloExecutado?.sumario || [];
  }, [protocoloExecutado]);

  const fhirEncounter = React.useMemo(() => {
    if (encounter.status === 'active' && encounter.data) {
      return encounter.data;
    }

    return {
      resourceType: 'Encounter',
    } as fhir4.Encounter;
  }, [encounter]);

  const resumo = React.useMemo(() => {
    if (protocoloExecutado && protocoloExecutado.sumario) {
      return truncate(
        protocoloExecutado.sumario
          .filter(item => item.status === 'completed')
          .map(item => item.texto_renderizado)
          .join(' '),
        { length: 280, omission: ' [...]' },
      );
    }

    return '';
  }, [protocoloExecutado]);

  const photo = React.useMemo(() => {
    if (user && user.empresa && user.empresa.logo) {
      return user.empresa.logo;
    }

    return undefined;
  }, [user]);

  const orientacoes: OrientacaoMedica[] = React.useMemo(() => {
    return solicitacoes.filter(
      item => item.tipo === 'orientacoes-medicas',
    ) as OrientacaoMedica[];
  }, [solicitacoes]);

  const encaminhamentos: EncaminhamentoEspecialista[] = React.useMemo(() => {
    return solicitacoes.filter(
      item => item.tipo === 'encaminhamento',
    ) as EncaminhamentoEspecialista[];
  }, [solicitacoes]);

  const handleCheck = React.useCallback((id: string | number) => {
    setExclude(prev => {
      if (prev.indexOf(id) === -1) {
        return [...prev, id];
      }
      return prev.filter(item => item !== id);
    });
  }, []);

  const checked = React.useCallback(
    (id: string | number) => {
      return exclude.indexOf(id) === -1;
    },
    [exclude],
  );

  const isAllUnchecked = React.useMemo(() => {
    return (
      !checked('resumo-protocolo') &&
      orientacoes.every(orientacao => !checked(orientacao.id)) &&
      encaminhamentos.every(encaminhamento => !checked(encaminhamento.id))
    );
  }, [checked, orientacoes, encaminhamentos]);

  const PdfDocument = React.useMemo(() => {
    return (
      <Document>
        {checked('resumo-protocolo') && (
          <DocumentPageResumoAtendimento
            encounter={fhirEncounter}
            sumario={sumario}
            solicitacoes={solicitacoes}
            photo={photo}
          />
        )}

        {orientacoes
          .filter(orientacao => checked(orientacao.id))
          .map(orientacao => (
            <DocumentPageOrientacao
              key={orientacao.id}
              resource={orientacao.pedido}
              encounter={fhirEncounter}
              photo={photo}
            />
          ))}

        {encaminhamentos.map(
          encaminhamento =>
            checked(encaminhamento.id) && (
              <DocumentPageEncaminhamento
                key={encaminhamento.id}
                resource={encaminhamento.pedido}
                encounter={fhirEncounter}
                photo={photo}
              />
            ),
        )}
      </Document>
    );
  }, [
    fhirEncounter,
    sumario,
    solicitacoes,
    photo,
    orientacoes,
    encaminhamentos,
    checked,
  ]);

  return (
    <ModalLayout
      style={{ content: { width: 582 } }}
      title="Impressão de documentos"
      isOpen={isOpen}
      onAfterClose={onClose}
    >
      <PrintSection>
        <h2>
          <FiClipboard size={20} />
          Resumo do atendimento
        </h2>

        <PrintItem
          title="Resumo do Protocolo"
          subtitle={resumo}
          onClick={() => handleCheck('resumo-protocolo')}
          selected={checked('resumo-protocolo')}
        />
      </PrintSection>

      {orientacoes.length > 0 && (
        <>
          <PrintSectionDivider />
          <PrintSection>
            <h2>
              <TipoArtefatoLabel
                tipo="orientacoes-medicas"
                label="Orientações para o paciente"
              />
            </h2>

            {orientacoes.map(orientacao => (
              <PrintItem
                key={orientacao.id}
                title={orientacao.pedido.title || 'Orientações médicas'}
                onClick={() => handleCheck(orientacao.id)}
                selected={checked(orientacao.id)}
              />
            ))}
          </PrintSection>
        </>
      )}

      {encaminhamentos.length > 0 && (
        <>
          <PrintSectionDivider />
          <PrintSection>
            <h2>
              <TipoArtefatoLabel tipo="encaminhamento" label="Encaminhamento" />
            </h2>

            {encaminhamentos.map(encaminhamento => (
              <PrintItem
                key={encaminhamento.id}
                title={
                  codeableConceptDisplay(encaminhamento.pedido, 'code') ||
                  'Encaminhamento p/ especialista'
                }
                onClick={() => handleCheck(encaminhamento.id)}
                selected={checked(encaminhamento.id)}
              />
            ))}
          </PrintSection>
        </>
      )}

      <PrintActions>
        <Button theme="secondary" onClick={onClose}>
          Voltar
        </Button>

        <Button
          loading={loadingPdf}
          theme="primary"
          disabled={isAllUnchecked}
          onClick={async () => {
            try {
              setLoadingPdf(true);

              await documentPdf(PdfDocument, {
                popup: true,
                printDialog: true,
              });

              setLoadingPdf(false);

              if (onClose) {
                onClose();
              }
              // eslint-disable-next-line no-empty
            } catch (error) {
            } finally {
              setLoadingPdf(false);
            }
          }}
        >
          Imprimir
        </Button>
      </PrintActions>
    </ModalLayout>
  );
};

export default ModalPrintSumario;
